/**
 * transport/frame.ts —— 下行帧解析（CONTRACT §3 帧**三型**：event／snapshot／command_ack）。
 * 纪律：帧形状按 dto.schema.json 校验（required 逐项、EventFrame additionalProperties:false）；
 *      载荷＝投影 DTO，**不由前端推断**；未知帧型 ⇒ 抛 frameInvalid（不吞）。
 * 🔴 加第三型**不放宽**既有校验：event／snapshot 的 required／键闭集逐条不变；
 *      `name` 非声明事件名仍**必须拒**（命令回执不入事件词域）。
 *
 * ★L-AU-FE-P01（审计整改·序2，合并修复「互有松紧、无一支配」）：
 *   · 松侧补齐（本件原缺、平行面 `projection/guards.ts` 有）：snapshot `views` 元素逐条
 *     按 ViewAssembly required 校验（schema `items.$ref: ViewAssembly`）；
 *     event `payload` 在场时做 **DTO 判别**——三型（ViewAssembly／RenderDesc／ApprovalCard）
 *     之一，或**扩展载荷** `{assembly: ViewAssembly, …}`（后端 `runtime/entry/model_catalog.py`
 *     `model_events()` 实况出口＝`{assembly, modelGroups, pointer}`，走声明名
 *     `platform.view.declared`）——判别不合 ⇒ 拒（不吞、不猜形状、不就地修补）。
 *   · 紧侧确认（平行面更严、本件保持）：`EventFrame.payload` 缺席＝合法（schema 非.required）；
 *     `name` 词域闭集；键闭集逐条不变。
 *   · 判别子＝本件 `checkEvent／checkSnapshot／checkCommandAck`（**全树唯一帧形状实现**）；
 *     形状基元（`isProjectionDto`／`isViewAssembly`）取自 `../projection`（DTO 守卫族），
 *     本件不复制 DTO 形状规则，亦不反向依赖 `isChannelFrame`（★L-AU-FE-A3 订正原
 *     「判别子＝isChannelFrame 族」之误述——该符号本件未用，属死引用）。
 */
import type { CommandAckFrame, EventFrame, Frame, SnapshotFrame } from "./dto";
import { DECLARED_EVENT_NAMES } from "./dto";
import { isProjectionDto, isViewAssembly } from "../projection";
import { frameInvalid } from "./errors";

const EVENT_KEYS = new Set(["id", "name", "payload"]);
const SNAPSHOT_KEYS = new Set(["cursor", "views"]);
/** 命令回执键闭集（逐字＝后端 `command_ingress.py::REPLY_KEYS`；★A-031 v1.7 起含 `client_id`） */
const COMMAND_ACK_KEYS = new Set(["id", "kind", "status", "reply", "event", "error", "client_id"]);
const COMMAND_ACK_STATUSES = new Set(["accepted", "rejected", "error"]);

/** JSON 文本 → Frame（形状不合约 ⇒ TransportError(E_FRAME_INVALID)） */
export function parseFrame(text: string): Frame {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw frameInvalid("非 JSON 文本");
  }
  return toFrame(raw);
}

/** 已解码对象 → Frame（判别唯一处：含 `views` ⇒ snapshot；含 `name` ⇒ event；含 `status` ⇒ command_ack） */
export function toFrame(raw: unknown): Frame {
  if (!isRecord(raw)) throw frameInvalid("帧非对象");
  if ("views" in raw) return checkSnapshot(raw);
  if ("name" in raw) return checkEvent(raw);
  return checkCommandAck(raw);
}

function checkCommandAck(raw: Record<string, unknown>): CommandAckFrame {
  // 判别式：不带 `name`／`views` 的帧按第三型收；形态不合约 ⇒ 仍拒（不放宽）
  if (!("status" in raw)) throw frameInvalid("帧不含 name／views／status：未知帧型");
  if (typeof raw.id !== "string" || !raw.id) throw frameInvalid("command_ack.id 缺失");
  if (typeof raw.kind !== "string" || !raw.kind) throw frameInvalid("command_ack.kind 缺失");
  if (typeof raw.status !== "string" || !COMMAND_ACK_STATUSES.has(raw.status)) {
    throw frameInvalid("command_ack.status 非闭集 {accepted,rejected,error}：" + String(raw.status));
  }
  for (const key of Object.keys(raw)) {
    if (!COMMAND_ACK_KEYS.has(key)) throw frameInvalid("command_ack 越出字段闭集：" + key);
  }
  const frame: CommandAckFrame = {
    id: raw.id, kind: raw.kind, status: raw.status as CommandAckFrame["status"],
  };
  for (const key of ["reply", "event", "error"] as const) {
    const value = raw[key];
    if (value === undefined) continue;
    if (!isRecord(value)) throw frameInvalid("command_ack." + key + " 非对象");
    frame[key] = value;
  }
  // ★A-031：上行关联键**原值回带**（键闭集内；值型仍校验——非串即拒，不放宽）
  if (raw.client_id !== undefined) {
    if (typeof raw.client_id !== "string") throw frameInvalid("command_ack.client_id 非字符串");
    frame.client_id = raw.client_id;
  }
  return frame;
}

function checkSnapshot(raw: Record<string, unknown>): SnapshotFrame {
  if (typeof raw.cursor !== "string") throw frameInvalid("snapshot.cursor 非字符串");
  if (!Array.isArray(raw.views)) throw frameInvalid("snapshot.views 非数组");
  for (const key of Object.keys(raw)) {
    if (!SNAPSHOT_KEYS.has(key)) throw frameInvalid("snapshot 越出字段闭集：" + key);
  }
  // ★L-AU-FE-P01（FE-P-02 补齐）：views 元素逐条按 ViewAssembly 校验
  // （schema `items.$ref: #/$defs/ViewAssembly`；原实现裸透传＝平行面指出之「松」侧）。
  if (!raw.views.every((v) => isViewAssembly(v))) {
    throw frameInvalid("snapshot.views 元素非 ViewAssembly（fact/slots 形状违约）");
  }
  return { cursor: raw.cursor, views: raw.views as SnapshotFrame["views"] };
}

function checkEvent(raw: Record<string, unknown>): EventFrame {
  if (typeof raw.id !== "string" || !raw.id) throw frameInvalid("event.id 缺失");
  if (typeof raw.name !== "string" || !raw.name) throw frameInvalid("event.name 缺失");
  // 收口适配（20261003 · main 收敛后）：形状唯一源＝projection/types（name ⊆ 声明清单，前端不自造）。
  if (!(DECLARED_EVENT_NAMES as readonly string[]).includes(raw.name)) {
    throw frameInvalid("event.name 非声明事件名：" + raw.name);
  }
  for (const key of Object.keys(raw)) {
    if (!EVENT_KEYS.has(key)) throw frameInvalid("event 越出字段闭集：" + key);
  }
  const frame: EventFrame = { id: raw.id, name: raw.name as EventFrame["name"] };
  if (raw.payload !== undefined) {
    if (!isRecord(raw.payload)) throw frameInvalid("event.payload 非对象");
    // ★L-AU-FE-P01（FE-P-06 补齐）：payload 在场 ⇒ DTO 判别（三型之一，或后端实况扩展载荷
    // `{assembly: ViewAssembly, …}`——`model_events()` 走声明名 `platform.view.declared`）。
    // 判别子＝projection 守卫族（唯一形状源）；不合 ⇒ 拒，不吞、不猜形状、不修补。
    if (!eventPayloadOk(raw.payload)) {
      throw frameInvalid("event.payload 非投影 DTO（三型判别＋assembly 扩展载荷均不合）");
    }
    frame.payload = raw.payload as EventFrame["payload"];
  }
  return frame;
}

/** event.payload 判别（★L-AU-FE-P01）：三型 DTO ∨ `{assembly: ViewAssembly, …}` 扩展载荷 */
function eventPayloadOk(payload: Record<string, unknown>): boolean {
  if (isProjectionDto(payload)) return true;
  return "assembly" in payload && isViewAssembly(payload["assembly"]);
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
